import { Table, Tooltip, Spin, Icon } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { PerEmpty } from '../../../components/custom_components/Empty'
import PerIcon from '../../../components/custom_components/Icon'
import { MeetingStatusModal, MeetingStatusModalData } from '../../../components/custom_components/MeetingStatusModal'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { PerTag } from '../../../components/custom_components/Tag'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useDateTime } from '../../../shared/useDateTime'
import {
  Country,
  EmployeeDisplayTinyInfo,
  MeetingPdfStatus,
  MeetingStatus,
  MeetingType,
  SkillEmployeeStatusEnum,
  MeetingStepViewId,
  SkipMeetingStepReason,
  SkillEmployeeStatusDto,
  MeetingHrFeedbackResult,
} from '../../../types'
import { getConclusionHrefByMeeting } from '../../meeting/util/hrefUtils'
import { ExtendedEmployeesAndAppraisalsFragment, OtherAppraisalFragment } from './appraisal-util'
import { appraisalTableUiProps } from './appraisalTable.uiprops'
import { ForceConfirmationModal, ForceConfirmationRefetchContext } from '../../meeting/ForceConfirmationModal'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { AppraisalInviteModalContainer } from './AppraisalInviteModalContainer'
import { useAppraisalsOfEmployeeByYear } from '../../../graphql/employee/hooks/useAppraisalsOfEmployeeByYear'
import { SkillStatusIcon } from '../../skillStatus/SkillStatusLabelComponent'
import useGetCountriesWithBranchOfficeVisibleFlag from '../../../graphql/countrySettings/hooks/useGetCountriesWithBranchOfficeVisibleFlag'
interface Props {
  noData: boolean
  filteredEmployees: ExtendedEmployeesAndAppraisalsFragment[]
  setTableData: React.Dispatch<React.SetStateAction<ExtendedEmployeesAndAppraisalsFragment[]>>
  tableData: ExtendedEmployeesAndAppraisalsFragment[]
  companyId?: string
  country?: Country
  isHrAdmin: boolean
  isCompanyManager: boolean
  buId?: string
}

export const CompanySettingsAppraisalTable: FC<Props> = ({
  filteredEmployees,
  setTableData,
  tableData,
  companyId,
  country,
  isHrAdmin,
  isCompanyManager,
  buId,
  noData,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, formatDateTimeShort } = useDateTime(user)
  const { data: branchOfficeVisibleFlags } = useGetCountriesWithBranchOfficeVisibleFlag()
  const countriesWhereOfficeIsVisible = new Set(
    (branchOfficeVisibleFlags?.getCountriesWithBranchOfficeVisibleFlag ?? [])
      .filter((item) => item.isBranchOfficeVisible)
      .map((item) => item.country),
  )
  const showOfficeColumn =
    countriesWhereOfficeIsVisible.has(user.country as Country) && (!country || countriesWhereOfficeIsVisible.has(country))
  const { mandatoryColor, mandatoryIcon, typeColor, statusColor, statusIcon, statusTooltip, pdfBtnTooltip } = appraisalTableUiProps(
    t,
    formatDateShort,
    formatDateTimeShort,
  )
  const [employeeToInvite, setEmployeeToInvite] = useState<string | null>(null)
  const [employeeSkillStatus, setEmployeeSkillStatus] = useState<SkillEmployeeStatusDto | null>(null)
  const [meetingStatusModalData, setMeetingStatusModalData] = useState<MeetingStatusModalData | null>(null)
  const [forceConfirmationModalData, setForceConfirmationModalData] = useState<ExtendedEmployeesAndAppraisalsFragment | null>(null)
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null)
  const [isRetrievingOtherAppraisals, toggleRetrievingOtherAppraisals] = useState<boolean>(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [getAppraisalsOfEmployeeByYear] = useAppraisalsOfEmployeeByYear()

  const toggleExpandRow = (record: ExtendedEmployeesAndAppraisalsFragment) => {
    setExpandedRowKeys((prev) => (prev.includes(record.id) ? prev.filter((k) => k !== record.id) : [...prev, record.id]))
  }

  const showMeetingModalStatus = (data: ExtendedEmployeesAndAppraisalsFragment) => {
    const allowedMeetings = [MeetingType.Appraisal, MeetingType.AppraisalUpdate]
    const meetingStatus = [MeetingStatus.Prepared, MeetingStatus.Draft]
    return (
      !!data.appraisal &&
      !!data.employee &&
      allowedMeetings.includes(data.appraisal.meetingType) &&
      meetingStatus.includes(data.appraisal.meetingStatus)
    )
  }

  const retrieveOtherAppraisals = async (record: ExtendedEmployeesAndAppraisalsFragment): Promise<boolean> => {
    const { data, loading, error } = await getAppraisalsOfEmployeeByYear({
      variables: {
        employeeId: record.employee.id,
        latestMeetingId: record.appraisal!.id,
        appraisalSeasonYear: record.appraisal!.appraisalSeason.year,
      },
    })
    if (!loading && !error && !!data) {
      const { id, ...recordData } = record
      const otherAppraisals = [...data.appraisalsOfEmployeeByYear]
        .slice(1)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map(
          (appraisal, index): OtherAppraisalFragment => ({
            ...recordData,
            status: appraisal.status,
            appraisal,
            isChild: true,
            key: `${appraisal.id}-child-${index}`,
          }),
        )
      const newTableData = tableData.map((row) => {
        if (row.id === record.id) {
          return { ...row, otherAppraisals }
        }
        return row
      })
      setTableData(newTableData)
      return true
    }
    return false
  }

  const getMeetingType = (data: ExtendedEmployeesAndAppraisalsFragment): MeetingType | undefined => {
    return !!data.appraisal ? data.appraisal.meetingType : undefined
  }

  const employeeTableHeader = (
    <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('companySettingsView.appraisalTab.table.employee.heading')}</Tooltip>
  )

  const onForceConfirmationModalCancel = () => {
    setForceConfirmationModalData(null)
  }

  const updateEmployeeSkillStatus = (employeeId: string, skillStatusData: SkillEmployeeStatusDto | null) => {
    const newTableData = tableData.map((row: ExtendedEmployeesAndAppraisalsFragment) => {
      if (row.employee.id === employeeId) {
        return { ...row, skillStatus: skillStatusData }
      }
      return row
    })
    setTableData(newTableData)
  }

  const columns = [
    {
      key: 'expandIcon',
      width: '4%',
    },
    {
      key: 'mandatory',
      width: '4%',
      className: 'textAlign-left',
      render: (data: ExtendedEmployeesAndAppraisalsFragment) => {
        return (
          !data.isChild && (
            <PerIcon
              tooltip={t(`companySettingsView.appraisalTab.mandatory.${data.mandatory}`)}
              name={mandatoryIcon(data.mandatory)}
              color={mandatoryColor(data.mandatory)}
              variant="decoration"
              size={20}
            />
          )
        )
      },
    },
    {
      key: 'employee',
      width: showOfficeColumn ? '14%' : '20%',
      title: employeeTableHeader,
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (rowDataA: ExtendedEmployeesAndAppraisalsFragment, rowDataB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (rowDataA.isChild || rowDataB.isChild) {
          return 0
        }
        const nameA = computeFullname(user.nameDisplayPreference, rowDataA.employee.firstName, rowDataA.employee.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowDataB.employee.firstName, rowDataB.employee.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (data: ExtendedEmployeesAndAppraisalsFragment) => {
        return (
          !data.isChild && (
            <PerAvatarPlus
              nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
              employeeId={data.employee.id}
              firstName={data.employee.firstName}
              lastName={data.employee.lastName}
              infoText={t(`employee.contract.level.${data.employee.currentLevel}`)}
              linkToProfile={
                data.employee.id !== user.employeeId
                  ? {
                      resigned: false,
                    }
                  : undefined
              }
              tooltip={data.employee.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
            />
          )
        )
      },
    },
    {
      key: 'skillStatus',
      width: '10%',
      className: 'textAlign-center',
      title: t('companySettingsView.appraisalTab.table.skillStatus.heading'),
      sorter: (employeeA: ExtendedEmployeesAndAppraisalsFragment, employeeB: ExtendedEmployeesAndAppraisalsFragment) => {
        const statusA = employeeA.skillStatus ? employeeA.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        const statusB = employeeB.skillStatus ? employeeB.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
        return statusA.localeCompare(statusB)
      },
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => {
        const skillStatus = rowData.skillStatus
        return (
          !rowData.isChild && (
            <PerFlexRow align="middle" justify="center">
              <SkillStatusIcon skillStatus={skillStatus} tooltipVisible />
              {!skillStatus && (
                <PerButtonCircle
                  ml="xxs"
                  color="secondary"
                  variant="pastel"
                  className="disable-shadow"
                  tooltip={t('profile.skillCenter.public.unknownIconTooltip')}
                  size="xsmall"
                  customIconComponent={<Icon type="info" style={{ fontSize: 12 }} />}
                />
              )}
            </PerFlexRow>
          )
        )
      },
    },
    {
      key: 'bu',
      width: '15%',
      title: t('companySettingsView.appraisalTab.table.bu.heading'),
      sorter: (rowDataA: ExtendedEmployeesAndAppraisalsFragment, rowDataB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (rowDataA.isChild || rowDataB.isChild) {
          return 0
        }
        const nameA = rowDataA.employee.buName
        const nameB = rowDataB.employee.buName
        return nameA.localeCompare(nameB)
      },
      render: (data: ExtendedEmployeesAndAppraisalsFragment) => {
        return (
          <div>
            {!data.isChild && (
              <PerFlexRow direction="column" justify="center">
                <PerText>{data.employee.buName}</PerText>
                {data.employee.managers && (
                  <>
                    {data.employee.managers.map((manager: EmployeeDisplayTinyInfo, index: number) => (
                      <PerText fontSize="xs" color="secondary" key={`bu_${index}`}>
                        {manager.firstName} {manager.lastName}
                      </PerText>
                    ))}
                  </>
                )}
              </PerFlexRow>
            )}
          </div>
        )
      },
    },
    ...(showOfficeColumn
      ? [
          {
            key: 'office',
            width: '9%',
            title: t('companySettingsView.appraisalTab.table.office.heading'),
            sorter: (rowDataA: ExtendedEmployeesAndAppraisalsFragment, rowDataB: ExtendedEmployeesAndAppraisalsFragment) => {
              if (rowDataA.isChild || rowDataB.isChild) {
                return 0
              }
              const officeA = rowDataA.branchOfficeCode ?? ''
              const officeB = rowDataB.branchOfficeCode ?? ''
              return officeA.localeCompare(officeB)
            },
            render: (data: ExtendedEmployeesAndAppraisalsFragment) => {
              return (
                <div>
                  {!data.isChild && (
                    <PerText fontSize="xs">
                      {data.branchOfficeCode ?? t('companySettingsView.appraisalTab.table.office.notAvailable')}
                    </PerText>
                  )}
                </div>
              )
            },
          },
        ]
      : []),
    {
      key: 'status',
      width: '7.5%',
      title: t('companySettingsView.appraisalTab.table.status.heading'),
      sorter: (statusA: ExtendedEmployeesAndAppraisalsFragment, statusB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (statusA.isChild || statusB.isChild) {
          return 0
        }
        return Number(statusA.status.sortOrder) - Number(statusB.status.sortOrder)
      },
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => (
        <>
          {rowData.appraisal ? (
            <PerFlexRow direction="column" align="middle" justify="center">
              <PerIcon
                tooltip={
                  rowData.appraisal.confirmation &&
                  rowData.appraisal.confirmation.forceConfirmedBy &&
                  rowData.appraisal.confirmation.forceConfirmedReason ? (
                    <>
                      <PerText>
                        {t(`appraisalMeeting.stepOverview.history.forceConfirmed`, {
                          managerFullName: computeFullname(
                            user.nameDisplayPreference,
                            rowData.appraisal.confirmation.forceConfirmedBy.firstName,
                            rowData.appraisal.confirmation.forceConfirmedBy.lastName,
                          ),
                          date: formatDateShort(rowData.appraisal.confirmation.date),
                          employeeFirstName: rowData.employee.firstName,
                        })}
                      </PerText>
                      <PerText>
                        {t(`appraisalMeeting.stepOverview.history.forceConfirmedReason`, {
                          reason: [
                            t('forceConfirmationModal.parentalLeave'),
                            t('forceConfirmationModal.longtermSickness'),
                            t('forceConfirmationModal.sabbatical'),
                            t('forceConfirmationModal.leaveOfAbsence'),
                          ].includes(rowData.appraisal.confirmation.forceConfirmedReason)
                            ? rowData.appraisal.confirmation.forceConfirmedReason
                            : t('forceConfirmationModal.other'),
                        })}
                      </PerText>
                    </>
                  ) : (
                    statusTooltip(
                      rowData.status.name,
                      rowData.employee.firstName,
                      rowData.appraisal.reviewer ? `${rowData.appraisal.reviewer.firstName} ${rowData.appraisal.reviewer.lastName}` : '',
                      rowData.appraisal.date,
                      !rowData.appraisal.reviewer,
                      rowData.appraisal.hrDisputeComment ? true : false,
                      rowData.appraisal.hrResult,
                    )
                  )
                }
                name={statusIcon(rowData.status.name)}
                color={statusColor(rowData.status.name)}
                variant="decoration"
                size={16}
              />
              {rowData.appraisal.confirmation &&
                rowData.appraisal.confirmation.forceConfirmedBy &&
                rowData.appraisal.confirmation.forceConfirmedReason && (
                  <PerText mt="xxxxs" color="secondary" fontSize="xs">
                    {t(`appraisalMeeting.stepOverview.history.forced`)}
                  </PerText>
                )}
            </PerFlexRow>
          ) : (
            <PerFlexRow direction="column" align="middle" justify="center">
              <PerIcon
                tooltip={statusTooltip(rowData.status.name, rowData.employee.firstName)}
                name={statusIcon(rowData.status.name)}
                color={statusColor(rowData.status.name)}
                variant="decoration"
                size={16}
              />
            </PerFlexRow>
          )}
        </>
      ),
    },
    {
      key: 'appraisalSeason',
      width: '7.5%',
      title: t('companySettingsView.appraisalTab.table.appraisalSeason.heading'),
      sorter: (rowDataA: ExtendedEmployeesAndAppraisalsFragment, rowDataB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (rowDataA.isChild || rowDataB.isChild) {
          return 0
        }
        const asA = rowDataA.appraisal ? rowDataA.appraisal.appraisalSeason.year.toString() : ''
        const asB = rowDataB.appraisal ? rowDataB.appraisal.appraisalSeason.year.toString() : ''
        return asA.localeCompare(asB)
      },
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => (
        <div>
          {rowData.appraisal && rowData.appraisal && (
            <PerFlexRow direction="column">
              <PerText>{rowData.appraisal.appraisalSeason.year}</PerText>
              {!!rowData.appraisal.companyNameOfMeeting && (
                <Tooltip title={rowData.appraisal.companyNameOfMeeting} placement="left">
                  <div style={{ width: '50px' }}>
                    <PerText fontSize="xs" color="secondary" className="truncate-text">
                      {rowData.appraisal.companyNameOfMeeting}
                    </PerText>
                  </div>
                </Tooltip>
              )}
            </PerFlexRow>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      width: showOfficeColumn ? '8%' : '9%',
      title: t('companySettingsView.appraisalTab.table.type.heading'),
      sorter: (rowDataA: ExtendedEmployeesAndAppraisalsFragment, rowDataB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (rowDataA.isChild || rowDataB.isChild) {
          return 0
        }
        const typeA = rowDataA.appraisal && rowDataA.appraisal.type ? rowDataA.appraisal.type.name : ''
        const typeB = rowDataB.appraisal && rowDataB.appraisal.type ? rowDataB.appraisal.type.name : ''
        return typeA.localeCompare(typeB)
      },
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => {
        const meetingType = getMeetingType(rowData)
        const getTooltipText = (skippedSteps: MeetingStepViewId[], reason: SkipMeetingStepReason | null) => {
          const skippedStepsString = skippedSteps.map((item) => t(`companySettingsView.appraisalTab.skippedSteps.${item}`))
          return (
            <>
              <PerText>
                {t('companySettingsView.appraisalTab.table.type.skippedSteps').concat(' ').concat(skippedStepsString.join(', '))}
              </PerText>
              {reason && (
                <PerText>
                  {t('companySettingsView.appraisalTab.table.type.reason')
                    .concat(' ')
                    .concat(t(`meetingInvite.reasonForReducedSteps.${reason}`))}
                </PerText>
              )}
            </>
          )
        }

        return (
          <PerFlexRow direction="column" align="middle" justify="center">
            {!!meetingType && (
              <PerText
                tooltip={
                  meetingType === MeetingType.AppraisalUpdate
                    ? t('companySettingsView.appraisalTab.table.meeting.status.au')
                    : t('companySettingsView.appraisalTab.table.meeting.status.a')
                }
              >
                {meetingType === MeetingType.AppraisalUpdate ? 'AU' : 'A'}
              </PerText>
            )}
            {rowData.appraisal && rowData.appraisal.type && rowData.appraisal.type.skippedSteps && (
              <>
                {rowData.appraisal.type.name && (
                  <PerFlexRow direction="column" align="middle" justify="center">
                    <Tooltip
                      title={
                        rowData.appraisal.type.skippedSteps.length
                          ? getTooltipText(rowData.appraisal.type.skippedSteps, rowData.appraisal.type.reason || null)
                          : undefined
                      }
                    >
                      <PerTag style={{ margin: '0' }} color={typeColor(rowData.appraisal.type.name)}>
                        {t(`companySettingsView.appraisalTab.type.${rowData.appraisal.type.name}`)}
                      </PerTag>
                    </Tooltip>
                  </PerFlexRow>
                )}
              </>
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'confirmation',
      width: showOfficeColumn ? '12%' : '14%',
      className: 'direction-column',
      title: t('companySettingsView.appraisalTab.table.confirmation.heading'),
      sorter: (confirmationDateA: ExtendedEmployeesAndAppraisalsFragment, confirmationDateB: ExtendedEmployeesAndAppraisalsFragment) => {
        if (confirmationDateA.isChild || confirmationDateB.isChild) {
          return 0
        }
        const dateA =
          confirmationDateA.appraisal && confirmationDateA.appraisal.confirmation ? confirmationDateA.appraisal.confirmation.date : ''
        const dateB =
          confirmationDateB.appraisal && confirmationDateB.appraisal.confirmation ? confirmationDateB.appraisal.confirmation.date : ''
        return dateA.localeCompare(dateB)
      },
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => {
        const renderBy = (type: 'reviewedBy' | 'discussedWith', by) => {
          return (
            <PerText fontSize="xs" color="secondary">
              {`${t(`companySettingsView.appraisalTab.table.confirmation.${type}`)} ${
                by && by.firstName && by.lastName ? `${by.firstName} ${by.lastName}` : t('user.unknown.name')
              }`}
            </PerText>
          )
        }
        return (
          <div style={{ flexDirection: 'column' }}>
            {rowData.appraisal && rowData.appraisal.confirmation && (
              <>
                <PerText>{formatDateShort(rowData.appraisal.confirmation.date)}</PerText>
                {renderBy('reviewedBy', rowData.appraisal.reviewer)}
                {rowData.appraisal.confirmation.discussedWith && renderBy('discussedWith', rowData.appraisal.confirmation.discussedWith)}
              </>
            )}
          </div>
        )
      },
    },
    {
      key: 'actions',
      width: '9%',
      title: t('companySettingsView.appraisalTab.table.actions.heading'),
      className: 'textAlign-left',
      render: (rowData: ExtendedEmployeesAndAppraisalsFragment) => {
        const conclusionPdfStatus = rowData.appraisal && rowData.appraisal.conclusionPdf && rowData.appraisal.conclusionPdf.status
        return (
          <PerTableActionButtons
            variant="pastel"
            justify="start"
            buttons={[
              {
                showOnlyWhen:
                  !rowData.isChild &&
                  (isCompanyManager || !!buId) &&
                  (!rowData.appraisal ||
                    [MeetingStatus.Confirmed].includes(rowData.appraisal.meetingStatus) ||
                    ([MeetingStatus.DisputedConfirmed].includes(rowData.appraisal.meetingStatus) &&
                      rowData.appraisal.hrResult !== MeetingHrFeedbackResult.NoResult)),
                icon: 'invite_fill',
                color: 'primary',
                tooltip: t('meetingInvite.headline'),
                onClick: (e: any) => {
                  e.preventDefault()
                  setEmployeeToInvite(rowData.employee.id)
                  setEmployeeSkillStatus(rowData.skillStatus || null)
                },
              },
              {
                showOnlyWhen:
                  !!rowData.appraisal &&
                  rowData.appraisal.meetingStatus === MeetingStatus.PendingConfirmation &&
                  (isCompanyManager || isHrAdmin),
                disabled: conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess,
                icon: 'chat_stroke',
                color: 'info',
                tooltip: t('managerMeetings.tooltip.forceConfirmation'),
                onClick: () => {
                  setForceConfirmationModalData(rowData)
                },
              },
              {
                showOnlyWhen: showMeetingModalStatus(rowData),
                icon: 'view_fill',
                color: 'warning',
                tooltip: t('appraisalStatusModal.tooltip'),
                onClick: () =>
                  setMeetingStatusModalData({
                    employeeId: rowData.employee.id,
                    meetingId: rowData.appraisal ? rowData.appraisal.id : null,
                    meetingType: rowData.appraisal ? rowData.appraisal.meetingType : MeetingType.Appraisal,
                    country: rowData.country,
                  }),
              },
              {
                showOnlyWhen:
                  rowData.status.name === 'Confirmed' || rowData.status.name === 'DisputedConfirmed' || rowData.status.name === 'Waiting',
                icon: 'view_fill',
                color: 'primary',
                tooltip: conclusionPdfStatus ? pdfBtnTooltip(conclusionPdfStatus) : undefined,
                asLink:
                  rowData.appraisal && rowData.appraisal.conclusionPdf && conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
                    ? getConclusionHrefByMeeting({
                        id: rowData.appraisal.id,
                        conclusionPdfFileName: rowData.appraisal.conclusionPdf.fileName,
                        conclusionPdfDownloadToken: rowData.appraisal.conclusionPdf.downloadToken,
                        conclusionPdfStatus: rowData.appraisal.conclusionPdf.status,
                      })
                    : undefined,
                disabled: !(conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess),
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      {forceConfirmationModalData && (
        <ForceConfirmationModal
          companyId={companyId!}
          country={country!}
          meetingId={forceConfirmationModalData.appraisal!.id}
          onClose={onForceConfirmationModalCancel}
          employeeFirstName={forceConfirmationModalData.employee.firstName}
          refetchContext={ForceConfirmationRefetchContext.CompanyAppraisalSettings}
        />
      )}
      {meetingStatusModalData && (
        <MeetingStatusModal
          employeeId={meetingStatusModalData.employeeId}
          meetingId={meetingStatusModalData.meetingId}
          meetingType={meetingStatusModalData.meetingType}
          onClose={() => {
            setMeetingStatusModalData(null)
          }}
          country={meetingStatusModalData.country}
        />
      )}
      {employeeToInvite && (
        <AppraisalInviteModalContainer
          employeeId={employeeToInvite}
          employeeSkillStatus={employeeSkillStatus}
          closeModal={() => {
            setEmployeeToInvite(null)
            setEmployeeSkillStatus(null)
          }}
          updateEmployeeSkillStatus={(skillStatusData: SkillEmployeeStatusDto | null) => {
            updateEmployeeSkillStatus(employeeToInvite, skillStatusData)
          }}
          buId={buId}
        />
      )}
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        dataSource={filteredEmployees.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        rowClassName={(row: ExtendedEmployeesAndAppraisalsFragment) => `minHeight-row ${!!row.isChild ? 'row-expanded' : ''}`}
        expandedRowKeys={expandedRowKeys}
        expandIcon={({ record }) => {
          if (record.isChild || !record.appraisal || record.appraisal.meetingType !== MeetingType.AppraisalUpdate) {
            return <></>
          }

          // loading spinner while fetching otherAppraisals
          if (expandedRecordId && expandedRecordId === record.id && isRetrievingOtherAppraisals) {
            return <Spin spinning />
          }

          const hasOther = !!record.otherAppraisals && record.otherAppraisals.length > 0

          return (
            <PerButtonCircle
              size="small"
              icon={expandedRowKeys.includes(record.id) ? 'remove_stroke' : 'add_stroke'}
              color="primary"
              variant="pastel"
              disabled={isRetrievingOtherAppraisals}
              onClick={async (e) => {
                e.stopPropagation()
                // appraisals already retrieved. Toggle rows opening only
                if (hasOther) {
                  toggleExpandRow(record)
                }
                // no appraisal fetched, yet. Toggle retrieval + open rows
                else {
                  setExpandedRecordId(record.id)
                  toggleRetrievingOtherAppraisals(true)
                  const otherAppraisalsRetrieved = await retrieveOtherAppraisals(record)
                  setExpandedRecordId(null)
                  toggleRetrievingOtherAppraisals(false)
                  if (otherAppraisalsRetrieved) {
                    toggleExpandRow(record)
                  }
                }
              }}
            />
          )
        }}
        childrenColumnName="otherAppraisals"
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty heading={noData ? t('table.emptyText') : t('companySettingsView.appraisalTab.table.emptyTable')} icon />,
        }}
      />
    </>
  )
}
